import React, { useEffect, useState } from 'react';
import { Button, InputNumber, Spin, Switch, Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { message } from '../../services/toastMessage';
import { getMyServiceProviderProfile, updateMyVisitFee } from '../../services/ServiceProviderProfileService';
import { MAX_VISIT_FEE_AMOUNT } from '../../interfaces/heavyDutyMachinery/IServiceProviderProfile';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { formatCurrency } from '../../helpers/currencyHelper';
import { TEAL, TEAL_SOFT_BG, CORAL, CORAL_SOFT_BG } from '../../theme/creamyTokens';

const { Text, Paragraph } = Typography;

/**
 * "¿Cobras por ir a verlo?"
 *
 * Two fields, and the reason it is its own screen rather than a checkbox
 * somewhere is what turning it on does to the deal. *"Pídeme presupuesto sin
 * compromiso"* and *"pídeme presupuesto, son 40 €"* are not the same product,
 * and the second one gets pressed far less often in a marketplace nobody has
 * heard of yet. So it is off until they say otherwise, and the consequence is
 * printed next to the switch instead of being left to be discovered: the visit
 * stops being a free preliminary and becomes version 1 of the quote, with the
 * customer's card authorised before anybody travels.
 *
 * The currency is never invented here -- it comes back from the server, read off
 * this professional's own listings, because a bare "40" on a pricing screen is
 * the kind of ambiguity that ends in a refund.
 */
const VisitFeeEditor: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();

  const [charges, setCharges] = useState(false);
  const [amount, setAmount] = useState<number | null>(null);
  const [currency, setCurrency] = useState('EUR');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  // No provider profile yet (or services switched off here). Nothing to set,
  // and nothing anybody did wrong -- so the block puts itself away, same as
  // AssistantGuideEditor.
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getMyServiceProviderProfile()
      .then((profile) => {
        if (cancelled) return;
        if (!profile) {
          setUnavailable(true);
          return;
        }
        setCharges(profile.chargesVisitFee);
        setAmount(profile.chargesVisitFee ? profile.visitFeeAmount : null);
        setCurrency(profile.visitFeeCurrencyCode || 'EUR');
      })
      .catch(() => {
        if (!cancelled) message.error(translate(t, TRANSLATIONS.VISIT_FEE_LOAD_ERROR));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    if (charges && (amount == null || amount <= 0)) {
      message.error(translate(t, TRANSLATIONS.VISIT_FEE_AMOUNT_REQUIRED));
      return;
    }
    if (charges && amount != null && amount > MAX_VISIT_FEE_AMOUNT) {
      message.error(translate(t, TRANSLATIONS.VISIT_FEE_MAX, { max: MAX_VISIT_FEE_AMOUNT }));
      return;
    }

    setSaving(true);
    try {
      const saved = await updateMyVisitFee(charges, charges ? (amount ?? 0) : 0);
      setCharges(saved.chargesVisitFee);
      setAmount(saved.chargesVisitFee ? saved.visitFeeAmount : null);
      setCurrency(saved.currencyCode || currency);
      setDirty(false);
      message.success(translate(t, TRANSLATIONS.VISIT_FEE_SAVED));
    } catch {
      message.error(translate(t, TRANSLATIONS.VISIT_FEE_SAVE_ERROR));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '28px 0' }}>
        <Spin />
      </div>
    );
  }

  if (unavailable) return null;

  return (
    <div style={{ maxWidth: 560 }}>
      <Paragraph type="secondary" style={{ fontSize: 13, marginBottom: 16 }}>
        {translate(t, TRANSLATIONS.VISIT_FEE_INTRO)}
      </Paragraph>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          padding: '12px 14px',
          borderRadius: 14,
          background: charges ? CORAL_SOFT_BG : token.colorFillQuaternary,
        }}
      >
        <Text strong style={{ fontSize: 14 }}>
          {translate(t, TRANSLATIONS.VISIT_FEE_SWITCH)}
        </Text>
        <Switch
          checked={charges}
          onChange={(checked) => {
            setCharges(checked);
            setDirty(true);
          }}
        />
      </div>

      {charges && (
        <div style={{ marginTop: 14 }}>
          <Text style={{ fontSize: 13, display: 'block', marginBottom: 6 }}>
            {translate(t, TRANSLATIONS.VISIT_FEE_AMOUNT_LABEL)}
          </Text>
          <InputNumber
            value={amount}
            min={0}
            max={MAX_VISIT_FEE_AMOUNT}
            step={5}
            precision={2}
            addonAfter={currency}
            onChange={(value) => {
              setAmount(value);
              setDirty(true);
            }}
            style={{ width: '100%', maxWidth: 240, borderRadius: 12 }}
          />
        </div>
      )}

      {/* The consequence, said out loud. A professional who turns this on
          expecting nothing else to change would be right to feel misled when
          the customer is asked for a card before the visit. */}
      <div
        style={{
          marginTop: 16,
          padding: '10px 14px',
          borderRadius: 14,
          fontSize: 13,
          background: charges ? CORAL_SOFT_BG : TEAL_SOFT_BG,
          color: charges ? CORAL : TEAL,
        }}
      >
        {translate(t, charges ? TRANSLATIONS.VISIT_FEE_ON_NOTE : TRANSLATIONS.VISIT_FEE_OFF_NOTE)}
      </div>

      <div style={{ marginTop: 16 }}>
        <Button type="primary" shape="round" loading={saving} disabled={!dirty} onClick={handleSave}>
          {translate(t, TRANSLATIONS.VISIT_FEE_SAVE)}
        </Button>
      </div>

      {/* What the customer will read, in their words rather than ours -- the
          same sentence the request screen shows them, so there is no gap
          between what was promised and what is charged. */}
      {charges && amount != null && amount > 0 && (
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 14, color: token.colorTextTertiary }}>
          {translate(t, TRANSLATIONS.SERVICE_VISIT_FEE_NOTICE, {
            amount: formatCurrency(amount, currency, i18n.language),
          })}
        </Text>
      )}
    </div>
  );
};

export default VisitFeeEditor;
